/**
 * The FoldMyCard mark on every card (asked for 2026-09-29): "FoldMyCard™ ·
 * www.foldmycard.com", in a band along the bottom of the closed card's back.
 *
 * The back is all pictures (zones 6-9), so the mark is printed on top of them,
 * on a light band so it reads over any photo. It is part of how the card is
 * drawn - the editor, every preview, the print page and the server's PDF all
 * draw it last - and no setting turns it off.
 *
 * A design can name the view (`brandView`: the fortune teller's poster) or the very spot
 * (`brandSpot`: the hexagonal packet's hem, on its front - the owner, 2026-10-25).
 */
import { invert, type Affine, type MarkSpot, type Pt, type TemplateDocument } from '@foldmycard/scene';

function inside([x, y]: Pt, poly: Pt[]) {
  let hit = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}

export const BRAND_TEXT = 'FoldMyCard™ · www.foldmycard.com';
/**
 * The view whose bottom edge carries the mark: the closed card, turned over -
 * or the view a design names (the fortune teller's poster, opened right out).
 */
const brandView = (template: TemplateDocument) => template.brandView ?? 'f2';

/** The band, `w` x `h` mm, centred on the origin: the words are 3 mm high on the usual 6.2 mm band. */
function Band({ w, h }: { w: number; h: number }) {
  return (
    <>
      <rect x={-w / 2} y={-h / 2} width={w} height={h} rx={h / 2} fill="#ffffff" fillOpacity={0.88} />
      <text x={0} y={0} textAnchor="middle" dominantBaseline="central" fontSize={(h * 3) / 6.2}
        fontFamily="Inter, 'Segoe UI', Arial, sans-serif" fontWeight={600} fill="#1b2a38">{BRAND_TEXT}</text>
    </>
  );
}

/** The band, drawn in the back view's own millimetres. */
export function BrandMark({ w, h }: { w: number; h: number }) {
  const bw = Math.min(w - 12, 62), bh = 6.2;
  return (
    <g className="brand-mark" pointerEvents="none" aria-label={BRAND_TEXT} transform={`translate(${w / 2} ${h - 6 - bh / 2})`}>
      <Band w={bw} h={bh} />
    </g>
  );
}

/**
 * The mark at the spot a design names (template.brandSpot - the hexagonal packet, 2026-10-25:
 * along the hem on its front, where the owner drew it), in the millimetres of the printed page.
 * A view draws it through the spot's pageToView.
 */
export function BrandAt({ spot }: { spot: MarkSpot }) {
  return (
    <g className="brand-mark" pointerEvents="none" aria-label={BRAND_TEXT}
      transform={`translate(${spot.at[0]} ${spot.at[1]}) rotate(${spot.angleDeg})`}>
      <Band w={spot.widthMm} h={spot.heightMm} />
    </g>
  );
}

/** Where the mark goes on the printed sheet: which side, and back-view mm -> sheet mm. */
export function brandOnSheet(template: TemplateDocument): { face: number; viewToPage: Affine } | null {
  if (template.brandSpot) return null;
  const view = template.views.find((v) => v.id === brandView(template));
  if (!view) return null;
  const spot: Pt = [view.widthMm / 2, view.heightMm - 9];
  const piece = view.pieces.find((pc) => inside(spot, pc.outline));
  const zone = piece && template.zones.find((z) => z.number === piece.zone);
  return piece && zone ? { face: zone.printFace, viewToPage: invert(piece.pageToView) } : null;
}

/** The view whose bottom edge carries the mark - none when the design names a spot for it. */
export const isBrandView = (template: TemplateDocument, id: string) => !template.brandSpot && id === brandView(template);
